import React, { useEffect, useState } from 'react';
import {
  Table,
  Input,
  Button,
  Breadcrumb,
  Modal,
  message,
  Card,
  Row,
  Col,
  Statistic,
  Form,
  Alert,
  Grid,
  Typography,
  Avatar,
  Tooltip,
  Progress,
  Select,
  Popconfirm,
  Space
} from 'antd';
import { Link } from 'react-router-dom';
import {
  SearchOutlined,
  RightOutlined,
  LinkOutlined,
  UserOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { synchronizationService, citiesService } from '../../common/services';
import Can from '../../components/can';
import dayjs from 'dayjs';

const { Text } = Typography;

// Couleur avatar dynamique
const getAvatarColor = (name) => {
  const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96', '#fa541c'];
  if (!name) return colors[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

// Initiales
const getInitials = (firstName, lastName) => {
  const f = firstName?.charAt(0)?.toUpperCase() || '';
  const l = lastName?.charAt(0)?.toUpperCase() || '';
  return f + l || '?';
};

const SyncChauffeurs = () => {
  // Liste des utilisateurs non liés
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setLoading] = useState(false);

  // Villes
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);

  // Statistiques
  const [stats, setStats] = useState({
    totalUsers: 0,
    usersWithDriver: 0,
    usersWithoutDriver: 0,
    syncRate: 0
  });
  const [statsLoading, setStatsLoading] = useState(false);

  // Recherche + pagination
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState('-createdAt');

  // Modal de liaison
  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [driverProfilId, setDriverProfilId] = useState('');
  const [isLinking, setIsLinking] = useState(false);

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const controlSize = isMobile ? 'small' : 'middle';

  const formatPhoneNumber = (phone) => {
    if (!phone) return '-';
    let local = phone.replace('+225', '').trim();
    return '+225 ' + local.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return dayjs(date).format('DD/MM/YYYY HH:mm');
  };

  // Charger les villes au montage
  useEffect(() => {
    citiesService.getAll()
      .then((res) => {
        const citiesData = res?.data || [];
        const activeCities = citiesData.filter(c => c.isActive);
        setCities(activeCities);
        const defaultCity = activeCities.find(c => c.isDefault);
        if (defaultCity) {
          setSelectedCity(defaultCity._id);
        } else if (activeCities.length > 0) {
          setSelectedCity(activeCities[0]._id);
        }
      })
      .catch((err) => console.error("Erreur chargement villes :", err));
  }, []);

  // Charger les statistiques
  const fetchStats = async () => {
    if (!selectedCity) return;
    try {
      setStatsLoading(true);
      const data = await synchronizationService.getStats(selectedCity);
      setStats(data);
    } catch (e) {
      console.error('Erreur chargement stats :', e);
    } finally {
      setStatsLoading(false);
    }
  };

  // Charger la liste des utilisateurs sans driver
  const fetchList = async () => {
    if (!selectedCity) return;
    try {
      setLoading(true);
      const params = { page, limit, sort, cityId: selectedCity };
      if (q && q.trim()) params.q = q.trim();

      const data = await synchronizationService.getUsersWithoutDriver(params);

      if (!data) {
        setItems([]);
        setTotal(0);
        return;
      }

      if (Array.isArray(data)) {
        setItems(data);
        setTotal(data.length);
      } else {
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } catch (e) {
      console.error('Erreur chargement utilisateurs :', e);
      message.error('Erreur lors du chargement de la liste');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCity]);

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, sort, q, selectedCity]);

  // Ouvrir la modal de liaison
  const openLinkModal = (user) => {
    setSelectedUser(user);
    setDriverProfilId('');
    setModalVisible(true);
  };

  // Fermer la modal
  const closeLinkModal = () => {
    setSelectedUser(null);
    setDriverProfilId('');
    setModalVisible(false);
  };

  // Effectuer la liaison
  const handleLink = async () => {
    if (!driverProfilId.trim()) {
      message.warning('Veuillez saisir un Driver Profile ID');
      return;
    }

    try {
      setIsLinking(true);
      const result = await synchronizationService.linkDriver({
        userId: selectedUser._id,
        driverProfilId: driverProfilId.trim()
      });

      message.success(result?.message || 'Chauffeur synchronisé avec succès !');
      closeLinkModal();
      fetchList();
      fetchStats();
    } catch (error) {
      console.error('Erreur lors de la liaison :', error);
      const errorMsg = error?.message || 'Une erreur est survenue lors de la synchronisation';
      message.error(errorMsg);
    } finally {
      setIsLinking(false);
    }
  };

  // Supprimer un utilisateur
  const handleDelete = async (userId) => {
    try {
      await synchronizationService.deleteUser(userId);
      message.success('Chauffeur supprimé avec succès');
      fetchList();
      fetchStats();
    } catch (error) {
      console.error('Erreur lors de la suppression :', error);
      const errorMsg = error?.message || 'Une erreur est survenue lors de la suppression';
      message.error(errorMsg);
    }
  };

  // Colonnes du tableau
  const columns = [
    {
      title: 'Chauffeur',
      key: 'name',
      render: (_, record) => {
        const fullName = `${record.firstName || ''} ${record.lastName || ''}`.trim() || 'N/A';
        const initials = getInitials(record.firstName, record.lastName);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Avatar
              size={isMobile ? 32 : 40}
              style={{ backgroundColor: getAvatarColor(fullName), fontWeight: 600 }}
            >
              {initials}
            </Avatar>
            <div>
              <div style={{ fontWeight: 500, color: '#262626' }}>{fullName}</div>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                {formatPhoneNumber(record.phone)}
              </div>
            </div>
          </div>
        );
      },
      sorter: true,
    },
    {
      title: 'Téléphone',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone) => (
        <span style={{ fontFamily: 'monospace', fontSize: 13 }}>
          {formatPhoneNumber(phone)}
        </span>
      ),
      sorter: true,
      responsive: ['lg'],
    },
    {
      title: 'Date inscription',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => (
        <Tooltip title={formatDate(date)}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <ClockCircleOutlined style={{ color: '#8c8c8c' }} />
            {dayjs(date).format('DD/MM/YYYY')}
          </span>
        </Tooltip>
      ),
      sorter: true,
      responsive: ['md'],
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Can perm="sync:update">
            <Button
              type="primary"
              icon={<LinkOutlined />}
              size={controlSize}
              onClick={() => openLinkModal(record)}
              style={{
                borderRadius: 8,
                background: 'linear-gradient(135deg, #722ed1 0%, #9254de 100%)',
                border: 'none'
              }}
            >
              {isMobile ? '' : 'Lier'}
            </Button>
          </Can>
          <Can perm="users:delete">
            <Popconfirm
              title="Supprimer ce chauffeur ?"
              description={`${record.firstName || ''} ${record.lastName || ''} sera définitivement supprimé.`}
              onConfirm={() => handleDelete(record._id)}
              okText="Supprimer"
              cancelText="Annuler"
              okButtonProps={{ danger: true }}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                size={controlSize}
                style={{ borderRadius: 8 }}
              >
                {isMobile ? '' : 'Supprimer'}
              </Button>
            </Popconfirm>
          </Can>
        </Space>
      ),
    },
  ];

  // Gestion du changement de tableau (pagination, tri)
  const onTableChange = (pagination, _filters, sorter) => {
    setPage(pagination.current || 1);
    setLimit(pagination.pageSize || 10);

    if (sorter?.field) {
      const field = sorter.field === 'name' ? 'firstName' : sorter.field;
      const dir = sorter.order === 'ascend' ? '' : '-';
      setSort(`${dir}${field}`);
    }
  };

  return (
    <div className="content-wrapper">
      <style>
        {`
          .sync-table .ant-table-thead > tr > th {
            background: #fafafa !important;
            font-weight: 600;
            color: #595959;
          }
          .sync-table .ant-table-tbody > tr:nth-child(odd) {
            background: #ffffff;
          }
          .sync-table .ant-table-tbody > tr:nth-child(even) {
            background: #fafafa;
          }
          .sync-table .ant-table-tbody > tr:hover > td {
            background: #f0f5ff !important;
          }
          .stat-card-sync {
            border-radius: 12px;
            border: none;
            box-shadow: 0 2px 8px rgba(0,0,0,0.06);
            transition: transform 0.2s, box-shadow 0.2s;
          }
          .stat-card-sync:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          }
        `}
      </style>

      <div className="container-xxl flex-grow-1" style={{ paddingTop: 8, paddingLeft: 24, paddingRight: 24 }}>
        {/* Breadcrumb */}
        <Breadcrumb
          separator={<RightOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />}
          style={{ marginBottom: 12, marginTop: 0, paddingTop: 0 }}
        >
          <Breadcrumb.Item>
            <Link to="/dashboard" style={{ color: '#722ed1' }}>Accueil</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span style={{ color: '#722ed1' }}>Configuration</span>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span style={{ color: '#595959' }}>Chauffeurs non liés</span>
          </Breadcrumb.Item>
        </Breadcrumb>

        {/* Cartes de statistiques */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <Card
              className="stat-card-sync"
              loading={statsLoading}
              bodyStyle={{ padding: 16 }}
              style={{ background: 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)' }}
            >
              <Statistic
                title={<span style={{ color: '#595959', fontWeight: 500 }}>Total chauffeurs</span>}
                value={stats.totalUsers}
                prefix={<UserOutlined style={{ color: '#1890ff' }} />}
                valueStyle={{ color: '#1890ff', fontWeight: 600 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card
              className="stat-card-sync"
              loading={statsLoading}
              bodyStyle={{ padding: 16 }}
              style={{ background: 'linear-gradient(135deg, #fff7e6 0%, #ffd591 100%)' }}
            >
              <Statistic
                title={<span style={{ color: '#595959', fontWeight: 500 }}>Non liés</span>}
                value={stats.usersWithoutDriver}
                prefix={<WarningOutlined style={{ color: '#fa8c16' }} />}
                valueStyle={{ color: '#fa8c16', fontWeight: 600 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card
              className="stat-card-sync"
              loading={statsLoading}
              bodyStyle={{ padding: 16 }}
              style={{ background: 'linear-gradient(135deg, #f6ffed 0%, #b7eb8f 100%)' }}
            >
              <Statistic
                title={<span style={{ color: '#595959', fontWeight: 500 }}>Liés à Yandex</span>}
                value={stats.usersWithDriver}
                prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ color: '#52c41a', fontWeight: 600 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card
              className="stat-card-sync"
              loading={statsLoading}
              bodyStyle={{ padding: 16 }}
              style={{ background: 'linear-gradient(135deg, #f9f0ff 0%, #d3adf7 100%)' }}
            >
              <div style={{ marginBottom: 8 }}>
                <span style={{ color: '#595959', fontWeight: 500, fontSize: 14 }}>Taux de sync</span>
              </div>
              <Progress
                percent={stats.syncRate || 0}
                strokeColor={{
                  '0%': '#722ed1',
                  '100%': '#b37feb',
                }}
                format={(percent) => <span style={{ fontWeight: 600, color: '#722ed1' }}>{Number(percent || 0).toFixed(1)}%</span>}
              />
            </Card>
          </Col>
        </Row>

        {/* Info Alert */}
        <Alert
          message="Synchronisation manuelle requise"
          description={
            <span>
              Ces chauffeurs se sont inscrits sur Tigoon mais n'ont pas encore de profil Yandex associé.
              Utilisez le bouton <strong>'Lier à Yandex'</strong> pour associer manuellement leur <code>driverProfilId</code>.
              <br />
              <strong style={{ color: '#fa8c16' }}>Note :</strong> Ces chauffeurs ont reçu 2000 points à l'inscription. Leur solde sera ajusté à 1000 points lors de la synchronisation.
            </span>
          }
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 24, borderRadius: 12 }}
        />

        {/* Carte avec filtre et tableau */}
        <Card
          style={{ borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
          bodyStyle={{ padding: isMobile ? 12 : 24 }}
        >
          {/* Barre de recherche et actions */}
          <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
            <Col xs={24} sm={8} md={6}>
              <Select
                placeholder="Sélectionner une ville"
                value={selectedCity}
                onChange={(val) => { setSelectedCity(val); setPage(1); }}
                style={{ width: '100%', borderRadius: 8 }}
                size={controlSize}
                suffixIcon={<GlobalOutlined style={{ color: '#722ed1' }} />}
              >
                {cities.map(city => (
                  <Select.Option key={city._id} value={city._id}>
                    {city.name} {city.isDefault && '(Par défaut)'}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={10} md={10}>
              <Input
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                placeholder="Rechercher par nom ou téléphone..."
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                style={{ width: '100%', borderRadius: 8 }}
                allowClear
                size={controlSize}
              />
            </Col>
            <Col xs={24} sm={6} md={8} style={{ display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'flex-start' : 'flex-end', gap: 12 }}>
              <div style={{ color: '#8c8c8c', fontSize: 13 }}>
                <SyncOutlined style={{ marginRight: 6 }} />
                {total} non lié{total > 1 ? 's' : ''}
              </div>
              <Button
                icon={<ReloadOutlined />}
                size={controlSize}
                onClick={() => { fetchList(); fetchStats(); }}
                style={{ borderRadius: 8 }}
              >
                Actualiser
              </Button>
            </Col>
          </Row>

          {/* Tableau */}
          <Table
            className="sync-table"
            rowKey="_id"
            columns={columns}
            dataSource={items}
            loading={isLoading}
            onChange={onTableChange}
            size={isMobile ? 'small' : 'middle'}
            pagination={{
              current: page,
              pageSize: limit,
              total,
              showSizeChanger: true,
              pageSizeOptions: [5, 10, 20, 50],
              showTotal: (total, range) => (
                <span style={{ color: '#8c8c8c' }}>
                  {range[0]}-{range[1]} sur {total}
                </span>
              ),
            }}
            locale={{
              emptyText: (
                <div style={{ padding: 40, textAlign: 'center' }}>
                  <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
                  <div style={{ color: '#52c41a', fontSize: 16, fontWeight: 500 }}>
                    Tous les chauffeurs sont synchronisés !
                  </div>
                  <div style={{ color: '#8c8c8c', marginTop: 8 }}>
                    Aucun chauffeur en attente de liaison Yandex
                  </div>
                </div>
              )
            }}
          />
        </Card>
      </div>

      {/* Modal de liaison */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Avatar
              size={40}
              style={{ backgroundColor: '#722ed1' }}
              icon={<LinkOutlined />}
            />
            <div>
              <div style={{ fontWeight: 600, fontSize: 16 }}>Lier à un profil Yandex</div>
              <div style={{ fontWeight: 400, fontSize: 12, color: '#8c8c8c' }}>
                Synchronisation manuelle
              </div>
            </div>
          </div>
        }
        open={isModalVisible}
        onCancel={closeLinkModal}
        footer={[
          <Button key="cancel" onClick={closeLinkModal} style={{ borderRadius: 8 }}>
            Annuler
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={isLinking}
            onClick={handleLink}
            icon={<SyncOutlined />}
            style={{
              borderRadius: 8,
              background: 'linear-gradient(135deg, #722ed1 0%, #9254de 100%)',
              border: 'none'
            }}
          >
            Synchroniser
          </Button>
        ]}
        centered
        destroyOnClose
        width={isMobile ? '95%' : 520}
      >
        {selectedUser && (
          <Form layout="vertical">
            {/* Info utilisateur */}
            <Card
              size="small"
              style={{
                marginBottom: 20,
                borderRadius: 12,
                background: 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)',
                border: '1px solid #d3adf7'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <Avatar
                  size={56}
                  style={{
                    backgroundColor: getAvatarColor(`${selectedUser.firstName} ${selectedUser.lastName}`),
                    fontWeight: 600,
                    fontSize: 20
                  }}
                >
                  {getInitials(selectedUser.firstName, selectedUser.lastName)}
                </Avatar>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 16, color: '#262626' }}>
                    {`${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim() || 'N/A'}
                  </div>
                  <div style={{ color: '#595959', fontFamily: 'monospace' }}>
                    {formatPhoneNumber(selectedUser.phone)}
                  </div>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                    Inscrit le {dayjs(selectedUser.createdAt).format('DD/MM/YYYY')}
                  </div>
                </div>
              </div>
            </Card>

            {/* Champ driverProfilId */}
            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Driver Profile ID (Yandex)</span>}
              required
            >
              <Input
                placeholder="Ex: a3611b7884024dd088595434c9dcad89"
                value={driverProfilId}
                onChange={(e) => setDriverProfilId(e.target.value)}
                size="large"
                maxLength={50}
                style={{ borderRadius: 8, fontFamily: 'monospace' }}
                prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
              />
              <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 6 }}>
                Identifiant unique du chauffeur dans le système Yandex
              </div>
            </Form.Item>

            <Alert
              message="Ajustement automatique"
              description={
                <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                  <li>Le solde sera ajusté à <strong>1000 points</strong> (au lieu de 2000)</li>
                  <li>Le <strong>CarId</strong> sera récupéré automatiquement depuis Yandex</li>
                </ul>
              }
              type="warning"
              showIcon
              style={{ borderRadius: 8 }}
            />
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default SyncChauffeurs;
